import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePortal } from '../contexts/PortalContext';
import { applyPortalBrand } from '../utils/applyPortalBrand';
import {
  IconDashboard,
  IconPackage,
  IconBuilding,
  IconLink,
  IconMail,
  IconChat,
  IconKey,
  IconMenu,
  IconClose,
  IconLogout,
  IconShield,
  IconCreditCard,
  IconActivity,
} from './ui/Icons';
import './Layout.css';

const OPERATOR_NAV = [
  { to: '/', end: true, label: 'Dashboard', icon: IconDashboard },
  { to: '/products', label: 'Products', icon: IconPackage },
  { to: '/partners', label: 'Partners', icon: IconBuilding },
  { to: '/tenants', label: 'Tenants', icon: IconBuilding },
  { to: '/leads', label: 'Leads & sources', icon: IconActivity },
  { to: '/tenant-products', label: 'Tenant Products', icon: IconLink },
  { to: '/email-config', label: 'Email Config', icon: IconMail },
  { to: '/whatsapp-config', label: 'WhatsApp Config', icon: IconChat },
  { to: '/whatsapp-notifications', label: 'WhatsApp Notifications', icon: IconChat },
  { to: '/oauth-config', label: 'OAuth Config', icon: IconKey },
  { to: '/platform-settings', label: 'Platform Settings', icon: IconShield },
  { to: '/plans', label: 'Plans', icon: IconPackage },
  { to: '/subscriptions', label: 'Subscriptions', icon: IconActivity },
  { to: '/revenue', label: 'Revenue', icon: IconDashboard },
  { to: '/payments', label: 'Payments', icon: IconCreditCard },
  { to: '/promo-codes', label: 'Promo codes', icon: IconPackage },
  { to: '/invoices', label: 'Invoices', icon: IconMail },
  { to: '/billing-settings', label: 'Billing Settings', icon: IconShield },
  { to: '/payment-providers', label: 'Payment Providers', icon: IconCreditCard },
];

const PARTNER_NAV = [
  { to: '/', end: true, label: 'Dashboard', icon: IconDashboard },
  { to: '/customers', label: 'Customers', icon: IconBuilding },
  { to: '/brand', label: 'Brand', icon: IconPackage },
  { to: '/usage', label: 'Billing & usage', icon: IconMail },
];

function Layout() {
  const { admin, logout, isPartnerAdmin } = useAuth();
  const { isPartnerPortal } = usePortal();
  const partnerMode = isPartnerPortal || isPartnerAdmin;
  const navItems = partnerMode ? PARTNER_NAV : OPERATOR_NAV;
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    applyPortalBrand(partnerMode ? admin?.tenant?.brand : null);
    return () => applyPortalBrand(null);
  }, [partnerMode, admin]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const handleResize = () => {
      if (window.innerWidth > 1024) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentPage = navItems.find((item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
  );

  return (
    <div className="app-shell">
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close menu"
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <div className="sidebar__logo">
            <IconShield width={22} height={22} />
          </div>
          <div className="sidebar__brand-text">
            <span className="sidebar__brand-name">
              {partnerMode
                ? (admin?.tenant?.brand?.appName || admin?.tenant?.name || 'Partner')
                : 'PennaiAppz'}
            </span>
            <span className="sidebar__brand-tag">{partnerMode ? 'Reseller' : 'Super Admin'}</span>
          </div>
          <button
            type="button"
            className="sidebar__close btn btn--ghost btn--icon"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Main navigation">
          {navItems.map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `sidebar__link ${isActive ? 'sidebar__link--active' : ''}`
              }
            >
              <Icon className="sidebar__link-icon" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar__footer">
          <div className="sidebar__user">
            <div className="sidebar__avatar" aria-hidden="true">
              {(admin?.email || 'A').charAt(0).toUpperCase()}
            </div>
            <div className="sidebar__user-info">
              <span className="sidebar__user-label">Signed in as</span>
              <span className="sidebar__user-email">{admin?.email}</span>
            </div>
          </div>
          <button type="button" onClick={handleLogout} className="sidebar__logout btn btn--ghost">
            <IconLogout />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <div className="app-main">
        <header className="topbar">
          <button
            type="button"
            className="topbar__menu btn btn--ghost btn--icon"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <IconMenu />
          </button>
          <div className="topbar__title">
            <h1>{currentPage?.label || (partnerMode ? 'Reseller' : 'Super Admin')}</h1>
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
